import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, ArrowLeft, Info } from 'lucide-react';
import { submitClaim } from '../../../Services/APIs/reimbursement';
import FormShell, { FormDone, focusFirstError, PRIMARY_BUTTON, SECONDARY_BUTTON, SECTION_HEADING } from './FormShell';
import { UploadField, UploadSlots, useUploads } from './UploadField';
import { ReviewFile } from './formFields';
import {
    formatInr, formatDate, toothLabel, statusLabel, proofChoices, selectedTotal, proofRounds, undecidedProcedures,
} from './reimbursementStatus';
import {
    CLAIM_STEP_LABELS, CLAIM_FIELD_ORDER, PROOF_XRAY_SLOTS, SUPPORTING_DOCUMENT_SLOTS, validateClaimStep, claimPayload,
    keepChoices, wizardSteps, fieldDomId,
} from './formHelpers';

const FORM_ID = 'claim';

// The four documents every proof needs, in the order the reviewer compares them with the prescription.
const REQUIRED_DOCUMENTS = [
    { slot: 'invoice_receipt', label: 'Invoice / receipt', hint: 'The bill or receipt from your clinic.' },
    { slot: 'post_treatment_upper_occlusal_photo', label: 'Upper Occlusal photo' },
    { slot: 'post_treatment_lower_occlusal_photo', label: 'Lower Occlusal photo' },
    { slot: 'post_treatment_problem_area_photo', label: 'Problem Area photo' },
];
const xrayLabel = (index) => `X-ray ${index + 1}`;
const documentLabel = (index) => `Supporting document ${index + 1}`;

const Note = ({ children }) => (
    <p className="flex gap-1.5 text-xs text-portal-sub">
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-px" aria-hidden="true" /> <span>{children}</span>
    </p>
);

// Proof of Treatment for an approved Reimbursement, in three steps: which approved procedures it is
// for (the treatments the member has had — the rest stay waiting for a later proof), the invoice and
// the same three photos as the prescription taken after the treatment (plus optional X-rays and
// supporting documents), and a review with the declaration. A Reimbursement approved before
// treatment plans has nothing to choose, so it starts at the documents. Only reachable while the
// Reimbursement is in Reimbursement Treatment Pending — enforced server-side, which answers 409 when
// a chosen procedure is no longer waiting for proof (`onCoverageChanged` then reloads it, and only
// the ticks still valid are kept). Re-uploads a reviewer asks for later go through ReuploadForm.
const ClaimForm = ({ reimbursement, onClose, onSuccess, onCoverageChanged }) => {
    const choices = proofChoices(reimbursement);
    const labels = choices.planned ? CLAIM_STEP_LABELS : CLAIM_STEP_LABELS.slice(1);
    const [step, setStep] = useState(1);
    const stepName = labels[step - 1];
    // Ids (as strings) of the procedures ticked; none to start with.
    const [selected, setSelected] = useState([]);
    const [errors, setErrors] = useState({});
    const [declarationAccepted, setDeclarationAccepted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState('');
    const [notice, setNotice] = useState('');
    // The proof number once it is submitted (shown on the done screen).
    const [submittedRound, setSubmittedRound] = useState(null);
    const { uploads, bind } = useUploads({ type: 'claim' });

    const round = proofRounds(reimbursement).length + 1;
    const undecided = undecidedProcedures(reimbursement).length;
    const id = (name) => fieldDomId(FORM_ID, name);
    const clearError = (name) => setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));

    // When the Reimbursement reloads (after a 409), keep only the ticks still waiting for proof, and
    // send the member back to the choice if any were dropped.
    const selectedRef = useRef(selected);
    selectedRef.current = selected;
    const reloading = useRef(false);
    const choiceKey = choices.procedures.map((p) => String(p._id)).join(',');
    useEffect(() => {
        const { kept, dropped } = keepChoices(selectedRef.current, choiceKey ? choiceKey.split(',') : []);
        if (dropped.length) setSelected(kept);
        if (reloading.current) {
            reloading.current = false;
            if (dropped.length) {
                setNotice(`We unticked ${dropped.length === 1 ? '1 procedure that is' : `${dropped.length} procedures that are`} no longer waiting for proof. Please check your choice.`);
                setStep(1);
            }
        }
    }, [choiceKey]);

    const toggle = (procedureId) => {
        setSelected((prev) => (prev.includes(procedureId) ? prev.filter((x) => x !== procedureId) : [...prev, procedureId]));
        clearError('procedures');
        setNotice('');
    };
    const chosen = choices.procedures.filter((p) => selected.includes(String(p._id)));
    const total = selectedTotal(choices.procedures, selected);

    const upload = (slot) => {
        const props = bind(slot);
        return { ...props, onPick: (file) => { clearError(slot); props.onPick(file); } };
    };

    const goBack = () => {
        setErrors({});
        setFormError('');
        setStep((s) => Math.max(s - 1, 1));
    };

    const submit = async () => {
        setIsSubmitting(true);
        setFormError('');
        setNotice('');
        try {
            await submitClaim(claimPayload({
                reimbursementId: reimbursement._id,
                uploads,
                procedureIds: choices.planned ? chosen.map((p) => p._id) : null,
            }));
            setSubmittedRound(round);
        } catch (err) {
            setFormError(err?.response?.data?.message || 'Failed to submit. Please try again.');
            // A chosen procedure is no longer waiting for proof (or the Reimbursement moved on):
            // reload it; the effect above keeps only the ticks still valid.
            if (err?.response?.status === 409) {
                reloading.current = true;
                onCoverageChanged?.();
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const onSubmit = (event) => {
        event.preventDefault();
        const stepErrors = validateClaimStep(stepName, { selected, planned: choices.planned, uploads, declarationAccepted });
        setErrors(stepErrors);
        if (Object.keys(stepErrors).length) {
            focusFirstError(FORM_ID, stepErrors, CLAIM_FIELD_ORDER);
            return;
        }
        setFormError('');
        if (step < labels.length) setStep(step + 1);
        else submit();
    };

    if (submittedRound) {
        return (
            <FormDone
                testId="proof-submitted"
                title={submittedRound > 1 ? `Proof #${submittedRound} uploaded` : 'Proof of treatment uploaded'}
                body="Your proof has been submitted successfully. Our team will review your treatment details next."
                status={statusLabel('reimbursement_treatment_review')}
                onDone={onSuccess}
            />
        );
    }

    const footer = (
        <div className="flex gap-3">
            {step > 1 && (
                <button type="button" onClick={goBack} disabled={isSubmitting} className={SECONDARY_BUTTON}>
                    <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back
                </button>
            )}
            {step < labels.length ? (
                <button type="submit" form={FORM_ID} className={PRIMARY_BUTTON}>
                    Next <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </button>
            ) : (
                <button type="submit" form={FORM_ID} disabled={isSubmitting} data-testid="claim-submit" className={PRIMARY_BUTTON}>
                    {isSubmitting ? 'Submitting…' : 'Submit'}
                </button>
            )}
        </div>
    );

    const optionalDocuments = [
        ...PROOF_XRAY_SLOTS.map((slot, index) => ({ slot, label: xrayLabel(index) })),
        ...SUPPORTING_DOCUMENT_SLOTS.map((slot, index) => ({ slot, label: documentLabel(index) })),
    ].filter(({ slot }) => uploads[slot]?.url);

    return (
        <FormShell
            title={round > 1 ? `Upload proof #${round}` : 'Upload proof of treatment'}
            subtitle={[reimbursement.clinic_name, reimbursement.createdAt && `requested ${formatDate(reimbursement.createdAt)}`].filter(Boolean).join(' · ')}
            steps={wizardSteps(labels, step)}
            stepsLabel="Proof of treatment steps"
            onClose={onClose}
            error={formError}
            footer={footer}
            scrollKey={step}
            testId="claim-form"
        >
            <form id={FORM_ID} onSubmit={onSubmit} noValidate className="space-y-4">
                {stepName === 'Procedures' && (
                    <>
                        {notice && <p role="status" className="text-xs text-portal-orange-ink bg-portal-orange-light border border-portal-orange-border rounded-xl p-3">{notice}</p>}
                        <fieldset aria-describedby={errors.procedures ? `${id('procedures')}-error` : undefined}>
                            <legend className={SECTION_HEADING}>Which treatments have you had?</legend>
                            <p className="text-xs text-portal-sub mt-1">Tick the approved procedures this proof is for.</p>
                            {choices.procedures.length > 0 ? (
                                <ul className="mt-3 space-y-2">
                                    {choices.procedures.map((p, index) => {
                                        const value = String(p._id);
                                        const checked = selected.includes(value);
                                        return (
                                            <li key={value}>
                                                <label className={`flex items-center gap-3 rounded-xl border px-3 py-3 cursor-pointer transition-colors ${checked
                                                    ? 'border-portal-purple bg-portal-purple-light'
                                                    : errors.procedures ? 'border-red-300 bg-white' : 'border-portal-border bg-white hover:bg-portal-bg'}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        id={index === 0 ? id('procedures') : undefined}
                                                        value={value}
                                                        checked={checked}
                                                        onChange={() => toggle(value)}
                                                        data-testid="proof-procedure"
                                                        aria-label={`${p.treatment} · ${toothLabel(p.tooth)} · ${formatInr(p.approved_amount)}`}
                                                        className="w-4 h-4 accent-portal-purple flex-shrink-0"
                                                    />
                                                    <span className="min-w-0 flex-1">
                                                        <span className="block text-sm font-semibold text-portal-ink">{p.treatment}</span>
                                                        <span className="block text-xs text-portal-sub">{toothLabel(p.tooth)}</span>
                                                    </span>
                                                    <span className="text-sm font-semibold text-portal-ink flex-shrink-0">{formatInr(p.approved_amount)}</span>
                                                </label>
                                            </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                <p className="mt-3 text-sm text-portal-sub">No approved procedures are waiting for proof right now.</p>
                            )}
                            {errors.procedures && (
                                <p id={`${id('procedures')}-error`} role="alert" className="text-xs text-red-700 mt-2">{errors.procedures}</p>
                            )}
                        </fieldset>

                        <div className="flex items-center justify-between gap-3 rounded-xl border border-portal-green-border bg-portal-green-light px-3 py-2.5 text-sm" data-testid="proof-selected-total" aria-live="polite">
                            <span className="text-portal-green-ink">Selected: {chosen.length} of {choices.procedures.length}</span>
                            <span className="font-bold text-portal-green-ink">{formatInr(total)}</span>
                        </div>
                        <div className="space-y-1.5">
                            <Note>Approved procedures you leave unticked keep waiting — you can add them on a later proof.</Note>
                            {undecided > 0 && (
                                <Note>
                                    {undecided === 1 ? '1 procedure is' : `${undecided} procedures are`} still waiting for a decision — you can add proof for {undecided === 1 ? 'it' : 'them'} once approved.
                                </Note>
                            )}
                        </div>
                    </>
                )}

                {stepName === 'Documents' && (
                    <>
                        <p className={SECTION_HEADING}>Documents</p>
                        <p className="text-xs text-portal-sub -mt-2">
                            Upload your invoice, and take these photos after your treatment, the same way as for your prescription.
                        </p>
                        {REQUIRED_DOCUMENTS.map(({ slot, label, hint }) => (
                            <UploadField key={slot} id={id(slot)} label={label} hint={hint} required testId={`upload-${slot}`} error={errors[slot]} {...upload(slot)} />
                        ))}

                        <p className={`${SECTION_HEADING} pt-2`}>X-rays (optional)</p>
                        <UploadSlots
                            slots={PROOF_XRAY_SLOTS}
                            uploads={uploads}
                            bind={bind}
                            labelFor={xrayLabel}
                            addLabel="Add another X-ray"
                            idFor={id}
                            testIdFor={(slot) => `upload-${slot}`}
                            errors={errors}
                            onPick={clearError}
                        />

                        <p className={`${SECTION_HEADING} pt-2`}>Supporting documents (optional)</p>
                        <p className="text-xs text-portal-sub -mt-2">Anything else that supports your reimbursement, such as a treatment summary.</p>
                        <UploadSlots
                            slots={SUPPORTING_DOCUMENT_SLOTS}
                            uploads={uploads}
                            bind={bind}
                            labelFor={documentLabel}
                            addLabel="Add another document"
                            idFor={id}
                            testIdFor={(slot) => `upload-${slot}`}
                            errors={errors}
                            onPick={clearError}
                        />
                    </>
                )}

                {stepName === 'Review' && (
                    <>
                        <section className="space-y-2">
                            <p className={SECTION_HEADING}>This proof is for</p>
                            {choices.planned ? (
                                <div className="rounded-xl border border-portal-green-border bg-portal-green-light p-3 text-xs" data-testid="claim-coverage">
                                    <ul className="space-y-1 text-portal-ink">
                                        {chosen.map((p) => (
                                            <li key={p._id} className="flex justify-between gap-3">
                                                <span className="min-w-0">{p.treatment} · {toothLabel(p.tooth)}</span>
                                                <span className="font-semibold flex-shrink-0">{formatInr(p.approved_amount)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <p className="flex justify-between gap-3 font-semibold text-portal-green-ink border-t border-portal-green-border mt-2 pt-2">
                                        <span>Approved for reimbursement</span>
                                        <span>{formatInr(total)}</span>
                                    </p>
                                </div>
                            ) : (
                                <p className="text-sm text-portal-ink" data-testid="claim-coverage">Your approved treatment.</p>
                            )}
                        </section>
                        <section className="space-y-2">
                            <p className={SECTION_HEADING}>Documents</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {REQUIRED_DOCUMENTS.map(({ slot, label }) => <ReviewFile key={slot} label={label} file={uploads[slot]?.file} />)}
                                {optionalDocuments.map(({ slot, label }) => <ReviewFile key={slot} label={label} file={uploads[slot]?.file} />)}
                            </div>
                        </section>

                        <div>
                            <label className="flex items-start gap-2.5 text-sm text-portal-ink">
                                <input
                                    type="checkbox"
                                    id={id('declaration')}
                                    checked={declarationAccepted}
                                    data-testid="claim-declaration"
                                    aria-invalid={errors.declaration ? 'true' : undefined}
                                    aria-describedby={errors.declaration ? `${id('declaration')}-error` : undefined}
                                    onChange={(e) => { setDeclarationAccepted(e.target.checked); clearError('declaration'); }}
                                    className="mt-0.5 w-4 h-4 accent-portal-purple flex-shrink-0"
                                />
                                <span>I declare that I have had the treatments above and that the documents are genuine.</span>
                            </label>
                            {errors.declaration && <p id={`${id('declaration')}-error`} className="text-xs text-red-700 mt-1.5 ml-6">{errors.declaration}</p>}
                        </div>
                    </>
                )}
            </form>
        </FormShell>
    );
};

export default ClaimForm;
